import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import LabelValuePair from "../common/LabelValuePair";
import TablePagination from "../common/Table/TablePagination";
import { AuditLog, AuditLogType, AuditLogTypeLabels } from "../../models/AuditLog";
import { MigrationType, MigrationTypeLabels } from "../../models/Statistics/MigrationType";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import OrganizationsRepository from "../../repositories/OrganizationsRepository";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { dialogWide } from "@/lib/dialogSize";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// The audit AdditionalData is serialized server-side with PascalCase keys and verbatim enum names
// (e.g. "HashMismatch", "ReplaceFromDisk"), so raw strings are humanized rather than looked up against the
// GUI's camelCase enums — same approach as ViewResolveConflictAuditLogDetailsModal.
const humanize = (value: unknown): string =>
  typeof value === "string" ? value.replace(/([a-z])([A-Z])/g, "$1 $2") : "—";

const formatCount = (value: unknown): string =>
  typeof value === "number" ? value.toLocaleString() : "—";

const formatSize = (megabytes: unknown): string =>
  typeof megabytes !== "number"
    ? "—"
    : megabytes >= 1024
      ? `${(megabytes / 1024).toFixed(2)} GB`
      : `${megabytes.toFixed(2)} MB`;

interface AppliedEntry {
  Family?: string;
  ConflictReason?: string;
  ResolutionAction?: string;
  ResolvedFileCount?: number;
  EntityCount?: number;
  TotalMegabytes?: number;
  OfficeFileCount?: number;
}

interface ResidualEntry {
  Family?: string;
  ConflictReason?: string;
  FileCount?: number;
  EntityCount?: number;
}

/** Maps the audit payload's verbatim enum name (e.g. "CasesMigration") back to the GUI's camelCase value. */
const familyFromAuditValue = (family: string | undefined): MigrationType | null => {
  if (!family) return null;
  const camel = family.charAt(0).toLowerCase() + family.slice(1);
  return (Object.values(MigrationType) as string[]).includes(camel)
    ? (camel as MigrationType)
    : null;
};

/** The short family name, falling back to a humanized enum name for anything unexpected in the payload. */
const familyLabel = (family: string | undefined): string => {
  const mapped = familyFromAuditValue(family);
  return mapped ? MigrationTypeLabels[mapped] : humanize(family);
};

/**
 * Details for a single org-wide bulk conflict resolution. One of these covers thousands of files, so the summary
 * payload the audit list already loads carries no per-file detail; that is held separately on the entry and
 * fetched only when the operator asks for it.
 */
const ViewOrgWideConflictResolutionAuditLogDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
  organizationId: string;
}> = ({ auditLog, open, onClose, organizationId }) => {
  const [drillDownFamily, setDrillDownFamily] = useState<MigrationType | null>(null);
  const [filesPage, setFilesPage] = useState(0);
  const [filesPerPage, setFilesPerPage] = useState(200);
  const organizationsRepo = new OrganizationsRepository();

  const parsed = (() => {
    if (!auditLog?.additionalData) return null;
    try {
      return JSON.parse(auditLog.additionalData);
    } catch {
      return null;
    }
  })();

  const applied: AppliedEntry[] = parsed?.Applied ?? [];
  const leftUnresolved: ResidualEntry[] = parsed?.LeftUnresolved ?? [];

  // Keyed on the audit entry itself. The affected files are an immutable snapshot taken when the resolution ran,
  // so this stays accurate even for files that have since conflicted again (which resets their conflict columns).
  const filesQuery = useQuery({
    queryKey: [
      QUERY_KEYS.ORG_CONFLICT_RESOLVED_FILES,
      organizationId,
      auditLog?.id,
      drillDownFamily,
      filesPage,
      filesPerPage,
    ],
    enabled: open && !!drillDownFamily && !!auditLog?.id,
    queryFn: () =>
      organizationsRepo.getConflictResolvedFiles(auditLog!.id, drillDownFamily, filesPage + 1, filesPerPage),
  });

  // Completed: every batch committed, even if the request failed afterwards. Stopped: it stopped part-way and the
  // entry holds what had committed. Neither: the entry was never amended — the process went away, or no outcome
  // write could be saved — so its counts are what the run set out to do, not what it did.
  const runState: "completed" | "stopped" | "unfinished" =
    parsed?.Completed ? "completed" : parsed?.Stopped ? "stopped" : "unfinished";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setDrillDownFamily(null);
          setFilesPage(0);
          onClose();
        }
      }}
    >
      <DialogContent className={`${dialogWide} overflow-y-auto`}>
        {auditLog ? (
          <>
            <DialogHeader>
              <DialogTitle>{AuditLogTypeLabels[AuditLogType.OrgWideConflictResolution]}</DialogTitle>
            </DialogHeader>

            <Separator />

            {parsed ? (
              // min-w-0 throughout: DialogContent is a grid and these are flex/grid children, which default to
              // min-width:auto — so a wide table stretches the whole dialog instead of scrolling inside its own
              // overflow-x wrapper.
              <div className="flex min-w-0 flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <div className="flex flex-row items-center gap-2">
                    <span className="text-sm font-bold">Status:</span>
                    <Badge variant={runState === "completed" ? "success" : "warning"}>
                      {runState === "completed" ? "Completed" : runState === "stopped" ? "Stopped part-way" : "Incomplete"}
                    </Badge>
                  </div>
                  {runState === "stopped" && (
                    <p className="text-xs text-muted-foreground">
                      The run stopped before it finished. The counts below are the files resolved until then; running
                      the same selection again resolves the rest.
                    </p>
                  )}
                  {runState === "unfinished" && (
                    <p className="text-xs text-muted-foreground">
                      The run did not report how far it got. The counts below are what it set out to do, not what it
                      did; check the entities for conflicts still open.
                    </p>
                  )}
                </div>
                <div className="grid min-w-0 grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                  <LabelValuePair
                    label={runState === "unfinished" ? "Files selected" : "Files resolved"}
                    value={formatCount(parsed.TotalResolvedFileCount)}
                  />
                  {/* Transfer-only: accepting the SharePoint copy or acknowledging resolves without uploading. */}
                  <LabelValuePair
                    label="Content to transfer"
                    value={formatSize(parsed.TotalQueuedMegabytes)}
                  />
                  <LabelValuePair
                    label="Scope"
                    value={
                      Array.isArray(parsed.Families)
                        ? parsed.Families.map(familyLabel).join(", ")
                        : "—"
                    }
                  />
                </div>

                <div>
                  <p className="mb-1 text-sm font-bold">Applied</p>
                  <div className="min-w-0 overflow-x-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Family</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Resolution</TableHead>
                          <TableHead className="text-right">Files</TableHead>
                          <TableHead className="text-right">Entities</TableHead>
                          <TableHead className="text-right">Office files</TableHead>
                          <TableHead className="text-right">Size</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {applied.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center text-muted-foreground">
                              Nothing was applied.
                            </TableCell>
                          </TableRow>
                        )}
                        {applied.map((entry, index) => (
                          <TableRow key={`${entry.Family}-${entry.ConflictReason}-${index}`}>
                            <TableCell className="whitespace-nowrap">{familyLabel(entry.Family)}</TableCell>
                            <TableCell>{humanize(entry.ConflictReason)}</TableCell>
                            <TableCell>{humanize(entry.ResolutionAction)}</TableCell>
                            <TableCell className="text-right">{formatCount(entry.ResolvedFileCount)}</TableCell>
                            <TableCell className="text-right">{formatCount(entry.EntityCount)}</TableCell>
                            <TableCell className="text-right">{formatCount(entry.OfficeFileCount)}</TableCell>
                            <TableCell className="text-right">{formatSize(entry.TotalMegabytes)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {leftUnresolved.length > 0 && (
                  <div>
                    <p className="mb-1 text-sm font-bold">Left unresolved</p>
                    <p className="mb-2 text-xs text-muted-foreground">
                      Reasons the operator deliberately did not action. These files were not changed.
                    </p>
                    <div className="min-w-0 overflow-x-auto rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Family</TableHead>
                            <TableHead>Reason</TableHead>
                            <TableHead className="text-right">Files</TableHead>
                            <TableHead className="text-right">Entities</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {leftUnresolved.map((entry, index) => (
                            <TableRow key={`${entry.Family}-${entry.ConflictReason}-${index}`}>
                              <TableCell className="whitespace-nowrap">{familyLabel(entry.Family)}</TableCell>
                              <TableCell>{humanize(entry.ConflictReason)}</TableCell>
                              <TableCell className="text-right">{formatCount(entry.FileCount)}</TableCell>
                              <TableCell className="text-right">{formatCount(entry.EntityCount)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                <div>
                  <p className="mb-1 text-sm font-bold">Affected files</p>
                  <p className="mb-2 text-xs text-muted-foreground">
                    A snapshot stored with this entry when the resolution ran, so it stays accurate for files that
                    have conflicted again, been renamed or been deleted since.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {applied
                      .map((entry) => familyFromAuditValue(entry.Family))
                      .filter((family, index, all): family is MigrationType =>
                        family !== null && all.indexOf(family) === index
                      )
                      .map((family) => (
                        <Button
                          key={family}
                          size="sm"
                          variant={drillDownFamily === family ? "default" : "outline"}
                          onClick={() => {
                            setDrillDownFamily(drillDownFamily === family ? null : family);
                            setFilesPage(0);
                          }}
                        >
                          {MigrationTypeLabels[family]}
                        </Button>
                      ))}
                  </div>

                  {drillDownFamily && (
                    <div className="mt-3">
                      {filesQuery.isLoading && (
                        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                          <Loader2 className="size-4 animate-spin" />
                          Loading affected files…
                        </div>
                      )}
                      {filesQuery.isError && (
                        <p className="py-4 text-sm text-muted-foreground">
                          Failed to load affected files.
                        </p>
                      )}
                      {filesQuery.data && (
                        <>
                          <div className="min-w-0 max-h-64 overflow-auto rounded-md border">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>File</TableHead>
                                  <TableHead>Reason</TableHead>
                                  <TableHead>Resolution</TableHead>
                                  <TableHead>Location</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {filesQuery.data.data.map((file) => (
                                  <TableRow key={file.fileId}>
                                    <TableCell className="font-mono text-xs">{file.filename}</TableCell>
                                    <TableCell>{humanize(file.conflictReason)}</TableCell>
                                    <TableCell>{humanize(file.conflictResolutionType)}</TableCell>
                                    <TableCell className="font-mono text-xs">
                                      {file.migrationEntityId}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                          {filesQuery.data.paging && filesQuery.data.paging.totalCount > 200 && (
                            <TablePagination
                              page={filesPage}
                              rowsPerPage={filesPerPage}
                              rows={filesQuery.data.paging.totalCount}
                              onPageChange={setFilesPage}
                              onRowsPerPageChange={(value) => {
                                setFilesPerPage(value);
                                setFilesPage(0);
                              }}
                              rowsPerPageOptions={[200, 500, 1000]}
                            />
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <span className="text-sm">Failed to parse additionalData</span>
            )}

            <Separator />

            <LabelValuePair label="Performed By" value={auditLog.performerEmailAddress} />
            <LabelValuePair label="Event Date" value={auditLog.eventDate} />
          </>
        ) : (
          <div />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewOrgWideConflictResolutionAuditLogDetailsModal;
